import React, { useState } from 'react';
import {
    Box,
    Chip,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Tooltip
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { useNotifications } from '../../utils/notificationUtils';

const PAYMENT_STATUS_OPTIONS = ['pending', 'paid', 'waived'];

const getPaymentStatusColor = (status) => {
    if (status === 'paid') return 'success';
    if (status === 'waived') return 'info';
    if (status === 'pending') return 'warning';
    return 'default';
};

const EnrollmentPaymentControls = ({
    enrollmentId,
    isHistorical = false,
    paymentStatus = 'pending',
    paymentMethod,
    paidAt,
    paymentNotes,
    disabled = false,
    compact = false,
    onUpdated
}) => {
    const { showSuccess, showError } = useNotifications();
    const [status, setStatus] = useState(paymentStatus || 'pending');
    const [notesOpen, setNotesOpen] = useState(false);
    const [notes, setNotes] = useState(paymentNotes || '');
    const [paidDate, setPaidDate] = useState(
        paidAt ? new Date(paidAt).toISOString().split('T')[0] : ''
    );
    const [saving, setSaving] = useState(false);

    const savePayment = async (updates) => {
        if (!enrollmentId) return;
        try {
            setSaving(true);
            await adminService.updateEnrollmentPayment(enrollmentId, {
                ...updates,
                isHistorical
            });
            if (updates.payment_status) setStatus(updates.payment_status);
            if (updates.payment_notes !== undefined) setNotes(updates.payment_notes);
            showSuccess('Payment updated');
            onUpdated?.();
        } catch (err) {
            showError(err.message || 'Failed to update payment');
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        const prev = status;
        setStatus(newStatus);
        try {
            setSaving(true);
            const payload = { payment_status: newStatus };
            if (newStatus === 'paid' && !paidDate) {
                payload.paid_at = new Date().toISOString();
            }
            if (newStatus === 'pending') {
                payload.paid_at = null;
            }
            await adminService.updateEnrollmentPayment(enrollmentId, {
                ...payload,
                isHistorical
            });
            showSuccess('Payment status updated');
            onUpdated?.();
        } catch (err) {
            setStatus(prev);
            showError(err.message || 'Failed to update payment status');
        } finally {
            setSaving(false);
        }
    };

    const handleNotesSave = async () => {
        const payload = { payment_notes: notes };
        if (paidDate) {
            payload.paid_at = new Date(paidDate).toISOString();
        }
        await savePayment(payload);
        setNotesOpen(false);
    };

    if (disabled || !enrollmentId) {
        return (
            <Chip
                label={paymentStatus || 'pending'}
                color={getPaymentStatusColor(paymentStatus)}
                size="small"
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: compact ? 100 : 110 }}>
                <Select
                    value={status || 'pending'}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={saving}
                    size="small"
                >
                    {PAYMENT_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                            {opt}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {paymentMethod && (
                <Chip label={paymentMethod} size="small" variant="outlined" />
            )}
            <Tooltip title="Payment notes">
                <IconButton size="small" onClick={() => setNotesOpen(true)} disabled={saving}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Payment notes</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label="Notes"
                        multiline
                        minRows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        label="Paid date"
                        type="date"
                        value={paidDate}
                        onChange={(e) => setPaidDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNotesOpen(false)}>Cancel</Button>
                    <Button onClick={handleNotesSave} variant="contained" disabled={saving}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EnrollmentPaymentControls;
export { getPaymentStatusColor, PAYMENT_STATUS_OPTIONS };
